<div class="card">
  <div class="card-header">
    <div class="row">
      <div class="col d-flex align-items-center">
        <span class="h5 mb-0">Document Details</span>
        <span class="badge badge-md ms-2" [ngClass]="reviewBadgeClass">{{
          detailsForDocumentReview!.reviewStatusDescription
        }}</span>
      </div>
      <div class="col text-end">
        @if (!isExpanded) {
          <span><i class="collapsed-icon clickable" (click)="toggleExpand()"></i></span>
        } @else {
          <span><i class="expanded-icon clickable" (click)="toggleExpand()"></i></span>
        }
      </div>
    </div>
  </div>
  <div
    class="card-body mx-2"
    #collapse="ngbCollapse"
    [ngbCollapse]="!isExpanded"
    (ngbCollapseChange)="isExpanded = $event"
  >
    <div class="row mb-2">
      <div class="col-lg">
        <label class="fw-bold" for="ein">EIN</label>
        @if (detailsForDocumentReview) {
          <p name="ein">{{ detailsForDocumentReview.ein }}</p>
        }
      </div>
      <div class="col-lg">
        <label class="fw-bold" for="name">Name</label>
        @if (detailsForDocumentReview) {
          <p name="name">{{ detailsForDocumentReview.educatorName }}</p>
        }
      </div>
      <div class="col-lg">
        <label class="fw-bold" for="altNames">Alternate Names</label>
        <div class="input-group"></div>
        <ng-template #alternateNamesPopoverContent>
          @for (alternateName of detailsForDocumentReview.alternateNames; track $index) {
            <p class="popover-spacing">{{ alternateName }}</p>
          }
        </ng-template>
        @if (detailsForDocumentReview && detailsForDocumentReview.alternateNames.length) {
          <a
            class="pointer clickable"
            [ngbPopover]="alternateNamesPopoverContent"
            popoverTitle="Alternate Names"
            certTooltip="Alternate Names"
          >
            View
          </a>
        } @else {
          <p name="altNames" class="">None</p>
        }
      </div>
      <div class="col-lg">
        <label class="fw-bold" for="dateOfBirth">Date of Birth</label>
        @if (detailsForDocumentReview) {
          <p name="dateOfBirth">{{ detailsForDocumentReview.dateOfBirth | date: 'MM/dd/yyyy' }}</p>
        }
      </div>
      <div class="col-lg">
        <label class="fw-bold" for="ssn">SSN</label>
        <div class="input-group">
          <ng-template #popoverBody>{{ detailsForDocumentReview.ssn! | SSN }}</ng-template>
          <a type="button" popoverTitle="SSN" class="clickable" [ngbPopover]="popoverBody" certTooltip="SSN">View</a>
        </div>
      </div>
      <div class="col">
        <label class="fw-bold" for="addresses">Addresses</label>
        <div class="input-group">
          <ng-template #addressesPopoverBody>
            @for (address of detailsForDocumentReview.addresses; track $index; let isLast = $last) {
              <div>
                <span>{{ address.line1 }}</span>
                @if (address.isActive && detailsForDocumentReview.addresses.length > 1) {
                  <span class="ms-1">
                    <i class="bi bi-star-fill text-primary" certTooltip="Active"></i>
                  </span>
                }
                @if (address.line2) {
                  <div>{{ address.line2 }}</div>
                }
                @if (!address.cityStateProvince) {
                  <span>{{ address.city }}, {{ address.stateCode }}</span>
                }
                @if (address.cityStateProvince) {
                  <span>{{ address.cityStateProvince }},</span>
                }
                {{ address.zipCode }}
                @if (address.zipPlusFourCode) {
                  <span> + {{ address.zipPlusFourCode }}</span>
                }
                @if (!isLast) {
                  <hr />
                }
              </div>
            }
          </ng-template>
          <a
            type="button"
            popoverTitle="Addresses"
            class="clickable"
            [ngbPopover]="addressesPopoverBody"
            certTooltip="Addresses"
            >View</a
          >
        </div>
      </div>
      <div class="col">
        <label class="fw-bold" for="docReceivedDate">Received Date</label>
        @if (detailsForDocumentReview) {
          <p name="docReceivedDate">
            {{ detailsForDocumentReview.documentReceivedDate | date: 'MM/dd/yyyy' }}
          </p>
        }
      </div>
    </div>
    <div class="row mb-2">
      <div class="col-lg-4">
        <label for="fileName" class="d-flex align-items-center gap-2 mb-1">
          <span>File Name</span>
          @if (isRenamePending) {
            <span
              class="badge bg-info"
              certTooltip="This document has been queued for renaming. No changes can be made to the filename at this time."
              >Pending Rename</span
            >
          }
        </label>
        <div class="d-flex align-items-center gap-2">
          <input
            type="text"
            class="form-control"
            id="fileName"
            name="fileName"
            [(ngModel)]="fileName"
            fileSystemRegex
            [disabled]="isRenamePending"
          />
        </div>
        @if (isRenamePending) {
          <small class="text-muted">Rename queued; file name changes disabled.</small>
        }
      </div>
      <div class="col-lg-4">
        <label for="documentTypeSelections">Document Types</label>
        <ng-select
          id="documentTypeSelections"
          name="documentTypeSelections"
          [closeOnSelect]="true"
          [multiple]="true"
          [(ngModel)]="documentTypeSelections"
          placeholder="Select document types..."
        >
          @for (option of documentTypeOptions; track $index) {
            <ng-option [value]="option">
              {{ option.description }}
            </ng-option>
          }
        </ng-select>
      </div>
      <div class="col-lg-4">
        <label for="reviewStatus">Review Status</label>
        <ng-select
          id="reviewStatus"
          name="reviewStatus"
          [closeOnSelect]="true"
          [multiple]="false"
          [(ngModel)]="reviewStatus"
          placeholder="Select review status..."
        >
          @for (option of reviewStatusOptions; track $index) {
            <ng-option [value]="option.value">
              {{ option.label }}
            </ng-option>
          }
        </ng-select>
      </div>
    </div>
    <div class="row mb-2">
      <div class="col">
        <label for="reviewComments">Review Comments</label>
        <textarea
          id="reviewComments"
          name="reviewComments"
          class="form-control"
          rows="2"
          [(ngModel)]="detailsForDocumentReview.reviewComments"
          placeholder="Enter review comments..."
        >
        </textarea>
      </div>
    </div>
    <div class="row">
      <div class="col text-end">
        <button type="submit" class="btn btn-primary" (click)="onSubmit()">Save</button>
      </div>
    </div>
  </div>
</div>
